import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronLeft, Hash } from "lucide-react";
import { QrPreviewButton } from "../../components/asset/QrLabelPreview";
import {
  useAsset,
  useCreateAsset,
  useUpdateAsset,
  useAssetCategories,
  useEntities,
  useAssetNumberPreview,
  useSystemConfig,
} from "../../hooks/useAssets";
import { Button, Input, Spinner } from "../../components/ui/index.jsx";
import {
  PAYMENT_SOURCE_OPTIONS,
  ASSET_STATUS_OPTIONS,
} from "../../components/asset/AssetHelpers.jsx";
import { cn } from "../../utils/cn";
import { DATA_ISSUE_LABELS, OWNERSHIP_OPTIONS } from "../../utils/constants";

// Same rule as the backend (utils/assetNumberFormat.js): the number's year is
// the year of the Entity Acquisition date; unknown = -1 = the "--" placeholder.
const yearOf = (d) => (d ? new Date(d).getUTCFullYear() % 100 : -1);

// ---------------------------------------------------------------------------
// Helpers — coerce numeric strings, treat "" and NaN as undefined
// ---------------------------------------------------------------------------
const coerceNum = (positive) =>
  z.preprocess(
    (v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    },
    positive ? z.number().positive().optional() : z.number().min(0).optional(),
  );

// ---------------------------------------------------------------------------
// Schema — "" treated as absent for ALL optional string fields
// ---------------------------------------------------------------------------
const optStr = z
  .string()
  .optional()
  .transform((v) => (v === "" ? undefined : v));

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  assetType: z.enum(["STANDARD", "STORAGE_UNIT"]).default("STANDARD"),
  entityId: z.string().min(1, "Entity is required"),
  categoryId: z
    .string()
    .min(1, "Category is required (needed for asset number)"),
  description: optStr,
  serialNumber: optStr,
  brand: optStr,
  model: optStr,
  isRestricted: z.boolean().default(false),
  status: z.string().default("ACTIVE"),
  location: optStr,
  purchaseDate: optStr,
  purchasePrice: coerceNum(true),
  entityPurchaseDate: optStr,
  entityPurchasePrice: coerceNum(true),
  ownership: z.enum(["OWNED", "RENTED_LEASED"]).default("OWNED"),
  installmentStartDate: optStr,
  installmentEndDate: optStr,
  installmentMonthlyAmount: coerceNum(true),
  dataNotes: optStr,
  paymentSource: optStr,
  vendor: optStr,
  invoiceNumber: optStr,
  usefulLifeMonths: coerceNum(true),
  salvageValue: coerceNum(false),
  warrantyStartDate: optStr,
  warrantyEndDate: optStr,
  warrantyProvider: optStr,
  warrantyNotes: optStr,
  storageCapacity: optStr,
  storageType: optStr,
  storageLocation: optStr,
});

// ---------------------------------------------------------------------------
// Section / Row
// ---------------------------------------------------------------------------
function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AssetForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const { data: existing, isLoading: loadingAsset } = useAsset(id, {
    enabled: isEdit,
  });
  const { data: categories = [] } = useAssetCategories();
  const { data: entities = [] } = useEntities();
  const { data: config } = useSystemConfig();
  // Provisional until the system-wide freeze (docs/adr/0001)
  const frozen = config?.assetNumbersFrozen ?? false;

  // Data-issue flags (empty = documented) are kept out of react-hook-form: a
  // plain checkbox group there can come back as a boolean or an array.
  const [issues, setIssues] = useState([]);
  // Entity Acquisition mirrors the Original until GA edits it directly (that
  // is the signal it's a buyback). Tracked separately for date and price.
  const [mirrorDate, setMirrorDate] = useState(true);
  const [mirrorPrice, setMirrorPrice] = useState(true);

  const createMutation = useCreateAsset();
  const updateMutation = useUpdateAsset(id);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      assetType: "STANDARD",
      status: "ACTIVE",
      isRestricted: false,
    },
  });

  // Pre-fill — wait for both asset data AND dropdown options to be loaded
  useEffect(() => {
    if (existing && isEdit && categories.length > 0 && entities.length > 0) {
      reset({
        name: existing.name ?? "",
        assetType: existing.assetType ?? "STANDARD",
        entityId: existing.entityId ?? "",
        categoryId: existing.categoryId ?? existing.category?.id ?? "",
        description: existing.description ?? "",
        serialNumber: existing.serialNumber ?? "",
        brand: existing.brand ?? "",
        model: existing.model ?? "",
        isRestricted: existing.isRestricted ?? false,
        status: existing.status ?? "ACTIVE",
        location: existing.location ?? "",
        purchaseDate: existing.purchaseDate?.slice(0, 10) ?? "",
        purchasePrice: existing.purchasePrice ?? "",
        entityPurchaseDate: existing.entityPurchaseDate?.slice(0, 10) ?? "",
        entityPurchasePrice: existing.entityPurchasePrice ?? "",
        ownership: existing.ownership ?? "OWNED",
        installmentStartDate: existing.installmentStartDate?.slice(0, 10) ?? "",
        installmentEndDate: existing.installmentEndDate?.slice(0, 10) ?? "",
        installmentMonthlyAmount: existing.installmentMonthlyAmount ?? "",
        dataNotes: existing.dataNotes ?? "",
        paymentSource: existing.paymentSource ?? "",
        vendor: existing.vendor ?? "",
        invoiceNumber: existing.invoiceNumber ?? "",
        usefulLifeMonths: existing.usefulLifeMonths ?? "",
        salvageValue: existing.salvageValue ?? "",
        warrantyStartDate: existing.warrantyStartDate?.slice(0, 10) ?? "",
        warrantyEndDate: existing.warrantyEndDate?.slice(0, 10) ?? "",
        warrantyProvider: existing.warrantyProvider ?? "",
        warrantyNotes: existing.warrantyNotes ?? "",
        storageCapacity: existing.storageCapacity ?? "",
        storageType: existing.storageType ?? "",
        storageLocation: existing.storageLocation ?? "",
      });
      setIssues(existing.dataIssues ?? []);
      // Keep mirroring only if the two acquisitions are still identical
      setMirrorDate(
        (existing.entityPurchaseDate?.slice(0, 10) ?? "") ===
          (existing.purchaseDate?.slice(0, 10) ?? ""),
      );
      setMirrorPrice(
        Number(existing.entityPurchasePrice ?? NaN) ===
          Number(existing.purchasePrice ?? NaN) ||
          (existing.entityPurchasePrice == null &&
            existing.purchasePrice == null),
      );
    }
  }, [existing?.id, isEdit, categories.length, entities.length]);

  const watchedEntityId = watch("entityId");
  const watchedCategoryId = watch("categoryId");
  const watchedAssetType = watch("assetType");
  const watchedOwnership = watch("ownership");
  const watchedInstallmentStart = watch("installmentStartDate");
  const watchedInstallmentEnd = watch("installmentEndDate");
  const installmentRangeInvalid =
    !!watchedInstallmentStart &&
    !!watchedInstallmentEnd &&
    watchedInstallmentEnd < watchedInstallmentStart;
  const watchedPurchaseDate = watch("purchaseDate");
  const watchedPurchasePrice = watch("purchasePrice");
  const watchedEntityPurchaseDate = watch("entityPurchaseDate");
  const watchedName = watch("name");

  useEffect(() => {
    if (mirrorDate) setValue("entityPurchaseDate", watchedPurchaseDate ?? "");
  }, [mirrorDate, watchedPurchaseDate, setValue]);
  useEffect(() => {
    if (mirrorPrice) setValue("entityPurchasePrice", watchedPurchasePrice ?? "");
  }, [mirrorPrice, watchedPurchasePrice, setValue]);

  // The number follows the ENTITY acquisition date, not the original one
  const { data: previewNumber } = useAssetNumberPreview(
    watchedEntityId,
    watchedCategoryId,
    watchedEntityPurchaseDate || undefined,
    isEdit ? id : undefined,
  );

  // On edit: would saving give this asset a different number? The sequence is
  // ranked by entity acquisition date within entity + category + year, so even
  // a date change inside the same year can move it.
  const numberWillChange =
    isEdit &&
    !frozen &&
    !!existing &&
    !!previewNumber &&
    previewNumber !== existing.assetNumber;

  async function onSubmit(data) {
    // Zod transform already strips undefined; remove null too
    const clean = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined && v !== null),
    );

    // Acquisition fields: blank means UNKNOWN (null), not "leave alone" — send
    // it explicitly so a cleared field really clears, and so an unknown
    // Entity Acquisition isn't silently copied from the original server-side.
    clean.purchaseDate = data.purchaseDate || null;
    clean.purchasePrice = data.purchasePrice ?? null;
    clean.entityPurchaseDate = data.entityPurchaseDate || null;
    clean.entityPurchasePrice = data.entityPurchasePrice ?? null;
    // Installment plan: blank = not recorded (null), sent explicitly so a
    // cleared field really clears. A rented/leased asset has no plan.
    clean.ownership = data.ownership || "OWNED";
    const rented = clean.ownership === "RENTED_LEASED";
    clean.installmentStartDate = rented ? null : data.installmentStartDate || null;
    clean.installmentEndDate = rented ? null : data.installmentEndDate || null;
    clean.installmentMonthlyAmount = rented ? null : data.installmentMonthlyAmount ?? null;
    clean.dataIssues = issues;
    clean.dataNotes = data.dataNotes || null;

    try {
      if (isEdit) {
        await updateMutation.mutateAsync(clean);
        navigate(`/assets/${id}`, { state: location.state });
      } else {
        const created = await createMutation.mutateAsync(clean);
        navigate(`/assets/${created.id}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
    }
  }

  const mutationError =
    createMutation.error?.response?.data?.error ||
    updateMutation.error?.response?.data?.error;

  if (isEdit && loadingAsset) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(isEdit ? `/assets/${id}` : (location.state?.from ?? "/assets"), isEdit ? { state: location.state } : undefined)}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? "Edit Asset" : "Add New Asset"}
          </h1>
          {isEdit && existing && (
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
              <Hash className="h-3.5 w-3.5" />
              Asset number:{" "}
              <span className="font-mono font-medium text-slate-700">
                {existing.assetNumber}
              </span>
              <span className="text-xs text-slate-400 ml-1">
                {frozen
                  ? "(permanent — changes only on entity transfer)"
                  : "(provisional — ranked by entity acquisition date within entity, category and year until numbers are frozen)"}
              </span>
            </p>
          )}
          {numberWillChange && previewNumber && (
            <p className="text-xs text-amber-600 mt-1">
              Saving will renumber this asset to{" "}
              <span className="font-mono font-medium">{previewNumber}</span>.
              Other assets in the affected entity/category/year groups may be
              renumbered too, so each group stays in acquisition-date order.
            </p>
          )}
          {!isEdit && watchedEntityId && watchedCategoryId && previewNumber && (
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
              <Hash className="h-3.5 w-3.5" />
              Next number:{" "}
              <span className="font-mono font-medium text-primary-600">
                {previewNumber}
              </span>
              <span className="text-xs text-slate-400 ml-1">
                {watchedEntityPurchaseDate
                  ? "(year from entity acquisition date)"
                  : "(year shows as -- until the entity acquisition date is set)"}
                {!frozen && " · provisional"}
              </span>
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Classification */}
        <Section title="Classification">
          <Row>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Entity <span className="text-rose-500">*</span>
              </label>
              <select
                {...register("entityId")}
                disabled={isEdit && frozen}
                className={cn(
                  "w-full rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500",
                  errors.entityId ? "border-rose-400" : "border-slate-300",
                  isEdit && frozen && "opacity-50 cursor-not-allowed",
                )}
              >
                <option value="">Select entity...</option>
                {entities.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.name}
                  </option>
                ))}
              </select>
              {errors.entityId && (
                <p className="text-xs text-rose-600 mt-1">
                  {errors.entityId.message}
                </p>
              )}
              {isEdit && frozen && (
                <p className="text-xs text-slate-400 mt-1">
                  Asset numbers are frozen — the entity now changes only through
                  a transfer.
                </p>
              )}
              {isEdit && !frozen && (
                <p className="text-xs text-slate-400 mt-1">
                  Correcting a wrong entity? Change it here — the number follows.
                  Add an "Entity unconfirmed" flag below if it's still a guess.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Category <span className="text-rose-500">*</span>
              </label>
              <select
                {...register("categoryId")}
                className={cn(
                  "w-full rounded-lg border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500",
                  errors.categoryId ? "border-rose-400" : "border-slate-300",
                )}
              >
                <option value="">Select category...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="text-xs text-rose-600 mt-1">
                  {errors.categoryId.message}
                </p>
              )}
            </div>
          </Row>

          <Row>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Asset Type
              </label>
              <select
                {...register("assetType")}
                disabled={isEdit}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="STANDARD">Standard Asset</option>
                <option value="STORAGE_UNIT">Storage Unit (HDD/NAS)</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Status
              </label>
              <select
                {...register("status")}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {ASSET_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </Row>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...register("isRestricted")}
              className="rounded border-slate-300 text-primary-600"
            />
            <span className="text-sm text-slate-700">
              Restricted asset (hidden from regular employees)
            </span>
          </label>
        </Section>

        {/* Asset Details */}
        <Section title="Asset Details">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">
                Name <span className="text-rose-500">*</span>
              </label>
              <span
                className={`text-xs tabular-nums ${
                  (watchedName?.length || 0) > 40
                    ? "text-rose-500 font-medium"
                    : (watchedName?.length || 0) > 32
                      ? "text-amber-500"
                      : "text-slate-400"
                }`}
              >
                {watchedName?.length || 0} / 40
              </span>
            </div>
            <input
              {...register("name")}
              placeholder="e.g. Godox SB-UE 80cm Octagon Honeycomb Grid"
              maxLength={60}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.name
                  ? "border-rose-400 focus:ring-rose-400"
                  : "border-slate-300"
              }`}
            />
            {errors.name && (
              <p className="text-xs text-rose-600 mt-1">
                {errors.name.message}
              </p>
            )}
            {(watchedName?.length || 0) > 40 && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠ Over 40 characters — label text may be truncated when printed
              </p>
            )}
            {!errors.name && (watchedName?.length || 0) <= 40 && (
              <p className="text-xs text-slate-400 mt-1">
                Recommended max 40 chars for clean label printing
              </p>
            )}
          </div>
          <Row>
            <Input
              label="Brand"
              placeholder="Apple, Dell, HP..."
              {...register("brand")}
            />
            <Input
              label="Model"
              placeholder="Model number or name"
              {...register("model")}
            />
          </Row>
          <Row>
            <Input
              label="Serial Number"
              placeholder="S/N from device"
              {...register("serialNumber")}
            />
            <Input
              label="Location"
              placeholder="Room, floor, office..."
              {...register("location")}
            />
          </Row>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Description
            </label>
            <textarea
              {...register("description")}
              rows={2}
              placeholder="Additional notes about this asset..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
        </Section>

        {/* Original Acquisition */}
        <Section title="Original Acquisition">
          <p className="text-xs text-slate-500 -mt-2">
            The first purchase from a vendor — if the CEO/Owner bought it
            personally, that purchase. Leave date/price blank if unknown (for
            example the invoice is still being searched for).
          </p>
          <Row>
            <Input
              label="Original Purchase Date"
              type="date"
              {...register("purchaseDate")}
            />
            <Input
              label="Original Purchase Price (IDR)"
              type="number"
              min="0"
              placeholder="0"
              {...register("purchasePrice")}
            />
          </Row>
          <Row>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Payment Source
              </label>
              <select
                {...register("paymentSource")}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">— Select —</option>
                {PAYMENT_SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Vendor"
              placeholder="Supplier name"
              {...register("vendor")}
            />
          </Row>
          <Input
            label="Invoice / PO Reference"
            placeholder="INV-2025-001 (upload PDF on asset detail page)"
            hint="To attach the invoice PDF, open the asset detail page after saving."
            {...register("invoiceNumber")}
          />
        </Section>

        {/* Entity Acquisition */}
        <Section title="Entity Acquisition">
          <p className="text-xs text-slate-500 -mt-2">
            When and at what price the <strong>current entity</strong> took
            ownership. For a direct purchase it's the same as the original, so
            it follows the fields above until you edit it here — for example
            when the entity bought the asset back from the CEO/Owner. It drives
            depreciation and the year in the asset number.
          </p>
          <Row>
            <div>
              <Input
                label="Entity Acquisition Date"
                type="date"
                {...register("entityPurchaseDate", {
                  onChange: () => setMirrorDate(false),
                })}
              />
              {mirrorDate && (
                <p className="text-xs text-slate-400 mt-1">
                  Same as original — edit to record a buyback.
                </p>
              )}
            </div>
            <div>
              <Input
                label="Entity Acquisition Price (IDR)"
                type="number"
                min="0"
                placeholder="0"
                {...register("entityPurchasePrice", {
                  onChange: () => setMirrorPrice(false),
                })}
              />
              {mirrorPrice && (
                <p className="text-xs text-slate-400 mt-1">
                  Same as original — edit to record a buyback.
                </p>
              )}
            </div>
          </Row>
          <p className="text-xs text-slate-400">
            One asset record per physical unit. If a bulk invoice doesn't give a
            price per unit, leave the price blank and flag "Price unconfirmed"
            below rather than estimating.
          </p>
        </Section>

        {/* Ownership */}
        <Section title="Ownership">
          <p className="text-xs text-slate-500 -mt-2">
            Whether the entity owns this asset or rents/leases it. For an owned
            asset bought on installment (for example on a credit card), record
            the plan; whether it counts as paid off follows from the end date.
          </p>
          <Row>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Ownership
              </label>
              <select
                {...register("ownership")}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {OWNERSHIP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </Row>
          {watchedOwnership !== "RENTED_LEASED" ? (
            <>
              <Row>
                <Input
                  label="Installment Start"
                  type="date"
                  {...register("installmentStartDate")}
                />
                <Input
                  label="Installment End"
                  type="date"
                  error={
                    installmentRangeInvalid
                      ? "End date cannot be before the start date"
                      : undefined
                  }
                  {...register("installmentEndDate")}
                />
              </Row>
              <Input
                label="Monthly Installment (IDR)"
                type="number"
                min="0"
                placeholder="0"
                hint="Optional. Leave all three blank if the asset was bought outright. Attach the installment form on the asset page after saving."
                {...register("installmentMonthlyAmount")}
              />
            </>
          ) : (
            <p className="text-xs text-slate-400">
              A rented or leased asset has no installment plan.
            </p>
          )}
        </Section>

        {/* Data Status */}
        <Section title="Data Status">
          <p className="text-xs text-slate-500 -mt-2">
            Flag why this asset's data can't be fully trusted yet. Several can
            apply at once; an asset with none is treated as documented.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(DATA_ISSUE_LABELS).map(([value, label]) => (
              <label
                key={value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={issues.includes(value)}
                  onChange={(e) =>
                    setIssues((prev) =>
                      e.target.checked
                        ? [...prev, value]
                        : prev.filter((v) => v !== value),
                    )
                  }
                  className="rounded border-slate-300 text-primary-600"
                />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Notes
            </label>
            <textarea
              {...register("dataNotes")}
              rows={2}
              maxLength={500}
              placeholder="What's unclear or missing, and who's chasing it…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
        </Section>

        {/* Depreciation */}
        <Section title="Depreciation">
          <Row>
            <Input
              label="Useful Life (months)"
              type="number"
              min="1"
              placeholder="e.g. 48 for 4 years"
              hint="Used for straight-line depreciation"
              {...register("usefulLifeMonths")}
            />
            <Input
              label="Salvage Value (IDR)"
              type="number"
              min="0"
              placeholder="0"
              hint="Residual value at end of useful life"
              {...register("salvageValue")}
            />
          </Row>
        </Section>

        {/* Warranty */}
        <Section title="Warranty">
          <Row>
            <Input
              label="Warranty Start"
              type="date"
              {...register("warrantyStartDate")}
            />
            <Input
              label="Warranty End"
              type="date"
              {...register("warrantyEndDate")}
            />
          </Row>
          <Input
            label="Warranty Provider"
            placeholder="Vendor, manufacturer..."
            {...register("warrantyProvider")}
          />
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Warranty Notes
            </label>
            <textarea
              {...register("warrantyNotes")}
              rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
        </Section>

        {/* Storage Unit */}
        {watchedAssetType === "STORAGE_UNIT" && (
          <Section title="Storage Unit Details">
            <Row>
              <Input
                label="Capacity"
                placeholder="e.g. 4TB, 8TB"
                {...register("storageCapacity")}
              />
              <Input
                label="Storage Type"
                placeholder="HDD, SSD, NAS, Cloud"
                {...register("storageType")}
              />
            </Row>
            <Input
              label="Physical Location"
              placeholder="Rack, shelf, room..."
              {...register("storageLocation")}
            />
          </Section>
        )}

        {/* QR Label Preview — only shown when editing (has asset ID) */}
        {isEdit &&
          (() => {
            // Build live label data from current form field values
            const liveEntity = entities.find((e) => e.id === watchedEntityId);
            const liveCategory = categories.find(
              (c) => c.id === watchedCategoryId,
            );
            const liveLabelData = existing
              ? {
                  assetId: id,
                  assetNumber: existing.assetNumber,
                  assetName: watchedName || existing.name || "—",
                  entityCode: liveEntity?.code || existing.entity?.code || "—",
                  entityColor:
                    liveEntity?.labelColor ||
                    existing.entity?.labelColor ||
                    "#0d1b4b",
                  categoryCode:
                    liveCategory?.code || existing.category?.code || "—",
                  qrDataUrl: null, // QrLabelModal will fetch this via API using assetId
                }
              : null;

            return (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      QR Label
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Preview reflects current unsaved field values
                    </p>
                  </div>
                  <QrPreviewButton
                    assetId={id}
                    labelDataOverride={liveLabelData}
                  />
                </div>
              </div>
            );
          })()}

        {/* Error banner */}
        {mutationError && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
            {mutationError}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(isEdit ? `/assets/${id}` : (location.state?.from ?? "/assets"), isEdit ? { state: location.state } : undefined)}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            {isEdit ? "Save Changes" : "Create Asset"}
          </Button>
        </div>
      </form>
    </div>
  );
}
